import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../../hooks/useContent';

export default function CTASection() {
  const { content } = useContent();
  const c = content?.cta;
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 0.9]);

  return (
    <section ref={sectionRef} style={{ padding: '100px 0', background: '#0D0D1F', position: 'relative', overflow: 'hidden' }}>
      {/* Parallax rotating sacred geometry */}
      <motion.div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        rotate, scale, pointerEvents: 'none'
      }}>
        <svg width="600" height="600" viewBox="0 0 600 600" opacity={0.05}>
          <circle cx="300" cy="300" r="280" stroke="#E8871A" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="200" stroke="#E8871A" strokeWidth="1" fill="none" />
          <circle cx="300" cy="300" r="120" stroke="#E8871A" strokeWidth="1" fill="none" />
          <polygon points="300,50 530,450 70,450" stroke="#E8871A" strokeWidth="1.5" fill="none" />
          <polygon points="300,550 70,150 530,150" stroke="#C9547A" strokeWidth="1.5" fill="none" />
          {[0,45,90,135,180,225,270,315].map((a, i) => (
            <line key={i}
              x1={300 + 270 * Math.cos(a * Math.PI / 180)}
              y1={300 + 270 * Math.sin(a * Math.PI / 180)}
              x2={300 + 290 * Math.cos(a * Math.PI / 180)}
              y2={300 + 290 * Math.sin(a * Math.PI / 180)}
              stroke="#E8871A" strokeWidth="2" />
          ))}
        </svg>
      </motion.div>

      {/* Pulsing glow */}
      <motion.div
        animate={{ scale: [1, 1.3, 1], opacity: [0.05, 0.12, 0.05] }}
        transition={{ duration: 4, repeat: Infinity }}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,135,26,0.15) 0%, transparent 70%)',
          pointerEvents: 'none'
        }}
      />

      <div className="container" style={{ position: 'relative', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="section-eyebrow">{c?.eyebrow ?? 'Begin Your Journey'}</p>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: 20 }}>
            {c?.title ?? 'Ready to Align'}<br />
            <motion.span style={{ color: '#E8871A', display: 'inline-block' }}
              animate={{ textShadow: ['0 0 20px rgba(232,135,26,0)', '0 0 30px rgba(232,135,26,0.5)', '0 0 20px rgba(232,135,26,0)'] }}
              transition={{ duration: 3, repeat: Infinity }}>
              {c?.titleHighlight ?? 'Your Space?'}
            </motion.span>
          </h2>
          <p style={{ fontSize: 16, opacity: 0.7, maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.8 }}>
            {c?.description}
          </p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link to={c?.primary?.path ?? '/contact'} className="btn-primary">{c?.primary?.label ?? 'Book Consultation'}</Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link to={c?.secondary?.path ?? '/services'} className="btn-outline">{c?.secondary?.label ?? 'Explore Services'}</Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
